/** How the preferred organization got selected (US 27702). */
export type PreferredOrganizationSource = 'migrated' | 'auto-single' | 'picker' | 'change';

/**
 * The one organization this BROWSER keeps local data for. Stored in localStorage under
 * 'edara3_preferred_organization', survives logout and is shared by every user who signs
 * in on this browser. Only the active organization's token carries the Edara tenant id,
 * so the record persists both identifiers together.
 */
export interface PreferredOrganizationRecord {
  keycloakOrgId: string;
  tenantId: string;
  name: string;
  /** ISO timestamp of when this organization became preferred. */
  setAt: string;
  source: PreferredOrganizationSource;
}

/** Pending (unsynced) Firestore record counts one user's session computed for itself. */
export interface PreferredOrgUserPendingState {
  salesOrders: number;
  cashTransactions: number;
  closures: number;
  total: number;
  /** ISO timestamp of the last recomputation. */
  updatedAt: string;
}

/**
 * Pending-data signal for the preferred organization, stored in localStorage under
 * 'edara3_preferred_org_sync_state'. Firestore records are per creator, so each
 * signed-in user's session recomputes and writes ONLY its own entry (queries for
 * other users' records may be denied by security rules). Offline customers and open
 * registers are browser/tenant-level, kept at the root. The aggregate over all of it
 * gates changing the preferred organization and feeds the counts-only message shown
 * to users the records don't belong to.
 */
export interface PreferredOrgSyncState {
  tenantId: string;
  /** ISO timestamp of the last write to this record. */
  updatedAt: string;
  users: { [email: string]: PreferredOrgUserPendingState };
  /** Dexie POS_Customers_V3 rows that only exist in this browser (no per-user split). */
  offlineCustomers: number;
  /** Open cash registers of the preferred tenant (any user). */
  openRegisters: number;
}

/** Live pending counts for the preferred organization. */
export interface UnsyncedSummary {
  salesOrders: number;
  cashTransactions: number;
  closures: number;
  offlineCustomers: number;
  openRegisters: number;
  total: number;
}

export type UnsyncedEntityKind = 'salesOrders' | 'cashTransactions' | 'closures';

export function emptyUnsyncedSummary(): UnsyncedSummary {
  return { salesOrders: 0, cashTransactions: 0, closures: 0, offlineCustomers: 0, openRegisters: 0, total: 0 };
}
